import { getLogger } from '@blueberry/observability'

const logger = getLogger('impact')

/**
 * Default tracker ID for signup events on Impact.
 */
const SIGNUP_TRACKER_ID = 61353

type ImpactConversionParams = {
  email: string // raw email; will be SHA-1 hashed here
}

declare global {
  interface Window {
    ire: (
      command: 'trackConversion',
      actionTrackerId: number,
      params: {
        orderId: string // same value as customerEmail
        customerId: string
        customerEmail: string // SHA-1 hex string per Impact spec
      },
      options?: { verifySiteDefinitionMatch: boolean },
    ) => void
  }
}

/**
 * SHA-1 hash to hex string using Web Crypto.
 * Impact expects a SHA-1 hex of the email.
 */
async function sha1Hex(input: string): Promise<string> {
  const data = new TextEncoder().encode(input.trim().toLowerCase())
  const digest = await crypto.subtle.digest('SHA-1', data)
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
}

/**
 * Check if Impact is initialized.
 */
function hasImpact(): boolean {
  if (typeof window === 'undefined') return false
  if (typeof window.ire !== 'function') {
    logger.warn('Impact (window.ire) not available')
    return false
  }
  return true
}

/**
 * Track a conversion with Impact. Email is automatically SHA-1 hashed.
 */
export async function impactTrackConversion({
  email,
}: ImpactConversionParams): Promise<void> {
  if (!hasImpact()) return

  try {
    const hashed = email ? await sha1Hex(email) : ''
    window.ire(
      'trackConversion',
      SIGNUP_TRACKER_ID,
      {
        orderId: hashed,
        customerId: '', // empty string is required by Impact
        customerEmail: hashed,
      },
      { verifySiteDefinitionMatch: true },
    )
  } catch (err) {
    logger.error('Impact trackConversion failed', err)
  }
}
